import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import DataTable from "../../components/common/DataTable";
import { getTransactions } from "../../services/transactionService";
import api from "../../services/api";

const typeIcon = {
  IMPS: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6d28d9" strokeWidth={2}><rect x="3" y="6" width="18" height="13" rx="2"/><path d="M3 10h18"/></svg>,
  UPI:  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f97316" strokeWidth={2}><path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>,
};

export default function MyService() {
  const navigate = useNavigate();
  const [serviceTab, setServiceTab] = useState("");
  const [myServices, setMyServices] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [wallet, setWallet] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 });

  useEffect(() => {
    api.get("/wallet/balance")
      .then((res) => setWallet(res.data?.data || null))
      .catch(() => {});
  }, []);

  // Fetch user's services from /services/my
  useEffect(() => {
    api.get("/services/my")
      .then((res) => {
        const services = res.data?.data || res.data || [];
        setMyServices(Array.isArray(services) ? services : []);
        if (services.length > 0) setServiceTab(services[0].service_id);
      })
      .catch(() => {});
  }, []);

  const fetchTransactions = useCallback(async (pg = page, lmt = limit, srch = search, serviceId = serviceTab) => {
    setLoading(true);
    try {
      const params = {
        page: pg,
        limit: lmt,
        ...(srch ? { search: srch } : {}),
        ...(serviceId ? { service_id: serviceId } : {}),
      };
      const res = await getTransactions(params);
      const d = res.data?.data || res.data || {};
      const rows = Array.isArray(d?.data)
        ? d.data
        : Array.isArray(d?.transactions)
          ? d.transactions
          : Array.isArray(d)
            ? d
            : [];
      setTransactions(rows);
      const p = d?.pagination || {};
      setPagination({
        current_page: Number(p.current_page) || pg,
        last_page: Number(p.last_page) || 1,
        total: Number(p.total) || rows.length
      });
    } catch {
      toast.error("Failed to fetch transactions");
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, serviceTab]);

  useEffect(() => {
    fetchTransactions(page, limit, search, serviceTab);
  }, [fetchTransactions, page, limit, search, serviceTab]);

  const handleSearch = (val) => {
    setSearch(val);
    setPage(1);
    fetchTransactions(1, limit, val);
  };

  const handleLimitChange = (newLimit) => {
    setLimit(Number(newLimit));
    setPage(1);
    fetchTransactions(1, newLimit, search);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.last_page) {
      setPage(newPage);
    }
  };

  const columns = [
    {
      accessorKey: "transaction_id",
      header: "KRESTAPAY ID",
      size: 260,
      Cell: ({ row }) => (
        <span className="text-[#4B2364] font-semibold text-[12px] font-mono hover:underline cursor-pointer">
          {row.original.transaction_id || row.original.id || "—"}
        </span>
      ),
    },
    {
      accessorKey: "created_at",
      header: "DATE & TIME",
      size: 140,
      Cell: ({ row }) => {
        const d = row.original.created_at ? new Date(row.original.created_at) : null;
        return (<>
          <div className="text-gray-600 text-xs whitespace-nowrap">
            {d ? d.toLocaleDateString() : "—"}<br />
            <span className="text-gray-400">{d ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}</span>
          </div>
        </>);
      },
    },
    {
      accessorKey: "partner_request_id",
      header: "PARTNER REQUEST ID",
      size: 200,
      Cell: ({ row }) => (
        <div className="flex items-center gap-1.5">
          <div className="w-6 h-6 rounded-md bg-gray-100 flex items-center justify-center flex-shrink-0">
            {typeIcon["IMPS"]}
          </div>
          <span className="text-gray-700 text-xs font-mono">{row.original.partner_request_id || "—"}</span>
        </div>
      ),
    },
    {
      accessorKey: "provider_ref",
      header: "PROVIDER REF",
      size: 160,
      Cell: ({ row }) => <span className="text-gray-600 text-xs font-mono">{row.original.provider_ref || "—"}</span>,
    },
    {
      accessorKey: "amount",
      header: "AMOUNT",
      size: 100,
      Cell: ({ row }) => (
        <span className="text-gray-900 font-bold text-sm">
          {row.original.amount != null ? `₹${row.original.amount}` : "—"}
        </span>
      ),
    },
    {
      accessorKey: "provider_message",
      header: "MESSAGE",
      size: 200,
      Cell: ({ row }) => <span className="text-xs text-green-600 font-medium">{row.original.provider_message || "—"}</span>,
    },
    {
      accessorKey: "status",
      header: "STATUS",
      size: 120,
      Cell: ({ row }) => {
        const s = (row.original.status || "").toUpperCase();
        const cls =
          s === "SUCCESS"  ? "bg-emerald-100 text-emerald-700" :
          s === "FAILED"   ? "bg-red-100 text-red-700" :
          s === "REFUNDED" ? "bg-amber-100 text-amber-600" :
                             "bg-blue-100 text-blue-600";
        return <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wide ${cls}`}>{s || "—"}</span>;
      },
    },
    {
      id: "actions",
      header: "ACTION",
      size: 90,
      Cell: ({ row }) => (
        <button
          title="View"
          onClick={() => navigate("/transaction-details", { state: { tx: row.original } })}
          className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#F3F4F5] hover:bg-[#E7E8E9] border-none cursor-pointer transition-colors"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#4C444E" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        </button>
      ),
    },
  ];

  return (
        <div className="flex-1 overflow-y-auto bg-gray-50 p-5">
          <div className="max-w-6xl mx-auto space-y-4">

            {/* Top bar */}
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-[36px] font-bold text-[#4B2364]">Service Wise Ledger Report</h1>
                <p className="text-[16px] font-medium text-[#4C444E] mt-0.5">Manage and monitor your multi-channel disbursement performance.</p>
              </div>
              <div className="flex gap-1 bg-[#EDEEEF] rounded-[8px]">
                {myServices.map(s => (
                  <button key={s.service_id} onClick={() => setServiceTab(s.service_id)}
                    className={`px-4 py-1.5 border-none rounded-md text-sm font-semibold transition-all cursor-pointer ${serviceTab === s.service_id ? "bg-white shadow-[0px_1px_2px_0px_#0000000D]" : "bg-transparent text-gray-500 hover:bg-gray-100"}`}
                  >{s.name}</button>
                ))}
              </div>
            </div>

            {/* Wallet Balance Card */}
            <div className="bg-[#4B2364] rounded-[24px] p-[24px] shadow-[0px_8px_10px_-6px_#0000001A,0px_20px_25px_-5px_#0000001A] flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-9 h-9 bg-white/20 rounded-[16px] flex items-center justify-center">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={2}><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20"/><circle cx="7" cy="15" r="1" fill="#fff"/></svg>
                  </div>
                  <span className="text-[12px] font-semibold text-[#FFFFFF99] uppercase tracking-wider">Wallet Balance</span>
                </div>
                <div className="text-[36px] font-bold text-white">₹{parseFloat(wallet?.available_balance || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</div>
                <div className="text-[13px] text-[#FFFFFFCC] mt-1">
                  Total: ₹{parseFloat(wallet?.total_balance || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })} &nbsp;|&nbsp; Hold: ₹{parseFloat(wallet?.hold_balance || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                </div>
              </div>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="1.5"><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20"/></svg>
            </div>

            {/* Table */}
            <div className="bg-white rounded-[32px] border border-[#CEC3D01A] shadow-[0px_20px_40px_0px_#4B236408] overflow-hidden pb-4">
              <div className="text-[#4B2364] p-[20px] border-b flex justify-between items-center border-[#EDEEEF] text-[18px] font-bold">
                <p>Transactions</p>
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <svg className="absolute left-3 top-1/2 -translate-y-1/2" width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="#9ca3af" strokeWidth="2"><circle cx="11" cy="11" r="8"></circle><path d="M21 21l-4.35-4.35" strokeLinecap="round"></path></svg>
                    <input
                      type="text"
                      placeholder="Search..."
                      value={search}
                      onChange={(e) => handleSearch(e.target.value)}
                      className="bg-[#F3F4F5] rounded-[10px] pl-9 pr-4 py-2 text-[13px] text-[#191C1D] border-none outline-none focus:ring-2 focus:ring-[#4B2364]/30 w-48 transition"
                    />
                  </div>
                  <select
                    value={limit}
                    onChange={(e) => handleLimitChange(e.target.value)}
                    className="bg-[#F3F4F5] rounded-[10px] px-3 py-2 text-[13px] text-[#191C1D] border-none outline-none focus:ring-2 focus:ring-[#4B2364]/30 cursor-pointer"
                  >
                    <option value="10">10 / page</option>
                    <option value="25">25 / page</option>
                    <option value="50">50 / page</option>
                    <option value="100">100 / page</option>
                  </select>
                </div>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="w-8 h-8 border-2 border-[#4B2364] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <DataTable columns={columns} data={transactions} />
              )}

              {/* Pagination */}
              {pagination.last_page > 1 && (
                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
                  <span className="text-xs text-[#4C444E]">Page {pagination.current_page} of {pagination.last_page}</span>
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => handlePageChange(page - 1)}
                      disabled={page === 1}
                      className="w-8 h-8 flex items-center justify-center rounded-lg border border-[#CEC3D0] bg-white text-[#4C444E] hover:bg-gray-50 cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 18l-6-6 6-6" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handlePageChange(page + 1)}
                      disabled={page === pagination.last_page}
                      className="w-8 h-8 flex items-center justify-center rounded-lg border border-[#CEC3D0] bg-white text-[#4C444E] hover:bg-gray-50 cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 18l6-6-6-6" />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
            </div>

          </div>
        </div>
  );
}
